import { setupFaqPrint } from "./faq-print.js";
import { matchesTerms, normalizeForSearch, searchTerms } from "./faq-search.js";

/**
 * よくあるご質問の検索・すべて開く／閉じる・設問へのリンク・開いた設問の計測（docs/features/01 §7）。
 *
 * ビュー側の約束（<c>SiteApp/pages/faq-page.tsx</c>・<c>faq-tools.tsx</c> が書き出す）:
 *   [data-faq]                     全体
 *   [data-faq-tools]               検索欄とボタンの行。<b>スクリプトが動くまで隠してある</b>
 *   [data-faq-search]              検索欄
 *   [data-faq-status]              当てはまった数（読み上げにも伝える）
 *   [data-faq-expand] / [data-faq-collapse]  すべて開く／すべて閉じる
 *   [data-faq-empty]               当てはまるものが無いときの案内（お問い合わせへ）
 *   [data-faq-category]            分類（section。id と値は分類の鍵）
 *   [data-faq-item]                設問（details。id と値は設問の鍵）
 *   [data-faq-toc]                 目次の 1 行（値は分類の鍵）
 *
 * <b>開閉そのものは <c>&lt;details&gt;</c> が持つ</b>（スクリプトが動かなくても開ける。Ctrl+F で閉じた答えも見つかる）。
 */

/** 設問 1 つ。 */
interface FaqItem {
    readonly element: HTMLDetailsElement;
    readonly key: string;
    readonly category: string;
    /** 設問と答えの文字を、比べる形にしたもの（faq-search.ts）。 */
    readonly text: string;
}

/** GA4 のイベント名（申し込みの完了の <c>portal_signup_completed</c> と同じ並び）。 */
export const faqOpenEvent = "portal_faq_open";

interface Analytics {
    gtag?: (...args: unknown[]) => void;
}

/**
 * 利用者が設問を開いたことを GA4 に送る。<b>送るのは設問と分類の鍵だけ</b>（文言・検索の言葉は送らない。個人の情報が入り得る）。
 * <c>gtag</c> は本番の書き出しにだけある（<c>analytics/analytics.ts</c>）。無ければ何もしない。
 */
const sendOpen = (item: FaqItem): void => {
    (globalThis as Analytics).gtag?.("event", faqOpenEvent, { faq_question: item.key, faq_category: item.category });
};

/** URL の # の値。<b>読めない値は空</b>（壊れた % を書いた URL で止めない）。 */
const hashValue = (hash: string): string => {
    try {
        return decodeURIComponent(hash.replace(/^#/u, ""));
    } catch {
        return "";
    }
};

function collectItems(root: HTMLElement): FaqItem[] {
    return [...root.querySelectorAll<HTMLDetailsElement>("details[data-faq-item]")].map((element) => ({
        element,
        key: element.dataset["faqItem"] ?? "",
        category: element.closest<HTMLElement>("[data-faq-category]")?.dataset["faqCategory"] ?? "",
        text: normalizeForSearch(element.textContent),
    }));
}

export const setupFaq = (root: ParentNode = document): void => {
    const faq = root.querySelector<HTMLElement>("[data-faq]");
    const items = faq === null ? [] : collectItems(faq);
    if (faq === null || items.length === 0) {
        return;
    }

    const one = (selector: string): HTMLElement | null => faq.querySelector<HTMLElement>(selector);
    const search = faq.querySelector<HTMLInputElement>("[data-faq-search]");
    const status = one("[data-faq-status]");
    const empty = one("[data-faq-empty]");
    const categories = [...faq.querySelectorAll<HTMLElement>("[data-faq-category]")];
    const tocRows = [...faq.querySelectorAll<HTMLElement>("[data-faq-toc]")];
    // 検索で開いたもの（言葉を消したら閉じ直す。利用者が開いたものは残す）
    const openedBySearch = new Set<FaqItem>();
    // 送った設問（ページを開いている間に 1 回だけ）
    const sent = new Set<string>();

    const showCategories = (): void => {
        for (const category of categories) {
            category.hidden = items.every((item) => item.category !== category.dataset["faqCategory"] || item.element.hidden);
        }
        for (const row of tocRows) {
            row.hidden = categories.find((o) => o.dataset["faqCategory"] === row.dataset["faqToc"])?.hidden ?? false;
        }
    };

    const filter = (query: string): void => {
        const terms = searchTerms(query);
        for (const item of items) {
            const matched = matchesTerms(item.text, terms);
            item.element.hidden = !matched;
            if (terms.length > 0 && matched && !item.element.open) {
                item.element.open = true;
                openedBySearch.add(item);
            }
            if (terms.length === 0 && openedBySearch.has(item)) {
                item.element.open = false;
            }
        }
        if (terms.length === 0) {
            openedBySearch.clear();
        }
        showCategories();
        const count = items.filter((item) => !item.element.hidden).length;
        if (empty !== null) empty.hidden = count > 0;
        if (status !== null) status.textContent = terms.length === 0 ? "" : `${String(count)} 件のご質問が当てはまりました。`;
    };

    const clearSearch = (): void => {
        if (search === null || search.value === "") {
            return;
        }
        search.value = "";
        filter("");
    };

    /** URL の # の設問を開いて見せる。分類なら、その分類へ送る。<b>検索で隠れていたら検索を外す</b>。 */
    const openFromHash = (): void => {
        const id = hashValue(location.hash);
        const item = items.find((o) => o.key === id);
        const category = categories.find((o) => o.id === id);
        const target = item?.element ?? category;
        if (target === undefined) {
            return;
        }
        if (target.hidden) {
            clearSearch();
        }
        if (item !== undefined) {
            // 開いても計測には数えない（利用者が押して開いたものだけを送る）
            item.element.open = true;
        }
        target.scrollIntoView({ block: "start" });
    };

    for (const item of items) {
        // summary を押した（Enter・Space も click になる）ときだけ送る。すべて開く・検索・# で開いたものは送らない
        item.element.querySelector("summary")?.addEventListener("click", () => {
            if (item.element.open || sent.has(item.key)) {
                return;
            }
            sent.add(item.key);
            sendOpen(item);
        });
    }

    one("[data-faq-expand]")?.addEventListener("click", () => {
        for (const item of items) if (!item.element.hidden) item.element.open = true;
    });
    one("[data-faq-collapse]")?.addEventListener("click", () => {
        for (const item of items) item.element.open = false;
        openedBySearch.clear();
    });
    search?.addEventListener("input", () => { filter(search.value); });
    window.addEventListener("hashchange", openFromHash);
    // 印刷のときは閉じた設問も開く（faq-print.ts）
    setupFaqPrint(items.map((item) => item.element));

    const tools = one("[data-faq-tools]");
    if (tools !== null) tools.hidden = false;
    openFromHash();
};
